import { Form } from 'antd'
import classNames from 'classnames'
import type { AgentGoPublishConfProps } from '@/features/agent/components/AgentGoPublishConf'
import { AgentGoPublishConf } from '@/features/agent/components/AgentGoPublishConf'
import { CopyBox } from '@/features/agent/components/CopyBox.tsx'
import { useAgentEdit } from '@/features/agent/provider/AgentEditProvider.tsx'
import { useQueryAgentGoApp } from '@/features/agent/hooks/useQueryAgentGoApp.ts'
import type { AuthAppDingTalk } from '@/apis/agentgo.ts'
import { Checkbox, Input } from '@/components'
import {
  DING_TALK_DOCS,
  PUBLISH_MODE,
} from '@/features/agent/AgentGo/common.tsx'
import { LinkText } from '@/features/agent/components/LinkText.tsx'
import { enableApp } from '@/apis/agentgo.ts'
import renderLabel from '@/features/agent/components/renderLabel.tsx'
import { AgentGoFormItem } from '@/features/agent/components/AgentGoFormItem.tsx'

interface DingTalkBindRequest {
  AppKey: string
  AppSecret: string
  auth_app_name: string
}

interface AgentGoDingTalkProps {
  mode?: string
  modeIcon?: string
  onBindSuccess?: () => void
  onUnBindSuccess?: () => void
}

export default function AgentGoDingTalk({
  mode,
  modeIcon,
  onBindSuccess,
  onUnBindSuccess,
}: AgentGoDingTalkProps) {
  const [form] = Form.useForm<DingTalkBindRequest>()
  const { applicationInfo } = useAgentEdit()

  const { runQueryApp, app, setApp, loading } =
    useQueryAgentGoApp<AuthAppDingTalk>({
      agentId: applicationInfo?.flowId,
      source: PUBLISH_MODE.dingtalk,
      onQuerySuccess: data => {
        const { auth_app_name, auth_app_config } = data
        form.setFieldsValue({
          auth_app_name,
          ...auth_app_config,
        })
      },
    })

  const handleComplete: NonNullable<
    AgentGoPublishConfProps['onComplete']
  > = appId => {
    onBindSuccess?.()
    if (appId && applicationInfo) {
      runQueryApp(applicationInfo.flowId)
    }
  }

  const steps = [
    {
      title: '填写钉钉应用凭证',
      disabled: true,
      content: (
        <>
          <div className='text-16px font-medium text-font'>
            登录{' '}
            <LinkText href='https://open-dev.dingtalk.com/'>
              钉钉开放平台
            </LinkText>{' '}
            获取信息
          </div>
          <p className='mt-8 text-12px text-#979797 font-normal mb-24'>
            注意！需要使用<span className='text-font'>企业管理员</span>
            的钉钉账号扫码登录
          </p>
        </>
      ),
    },
    {
      title: '配置消息接收地址',
      disabled: true,
      content: (
        <>
          <div className='text-16px font-medium'>
            复制以下地址，到{' '}
            <LinkText href='https://open-dev.dingtalk.com/'>
              钉钉开放平台
            </LinkText>{' '}
            完成机器人配置并发布
          </div>
          <p className='mt-8 text-12px text-#979797 font-normal'>
            在钉钉应用开发-应用能力中可找到机器人
          </p>
          <Form.Item
            shouldUpdate={(prev, curr) => prev.AppKey !== curr.AppKey}
            noStyle
          >
            {({ getFieldValue }) => {
              const app_key = getFieldValue('AppKey')
              return (
                <>
                  <div className='font-medium mt-24 line-height-16px'>
                    消息接收地址
                  </div>
                  <CopyBox
                    className='mt-8 mb-24'
                    // prettier-ignore
                    value={`${import.meta.env.VITE_AI_API_BASE_URL}/v1/webhook/agent/dingtalk_orgapp_robot/${app_key}/receive_message`}
                  />
                </>
              )
            }}
          </Form.Item>
        </>
      ),
    },
    {
      title: '发布钉钉应用',
      disabled: true,
      content: (
        <div className='text-16px font-medium text-font mb-24'>
          在{' '}
          <LinkText href='https://open-dev.dingtalk.com/'>
            钉钉开放平台
          </LinkText>{' '}
          版本管理中发布当前应用
        </div>
      ),
    },
  ]

  const handleValuesChange: NonNullable<
    AgentGoPublishConfProps['onValuesChange']
  > = changedValues => {
    if ('is_enable' in changedValues) {
      setApp({ ...app, is_enable: changedValues.is_enable })
      enableApp(app.auth_app_id!, changedValues.is_enable)
    }
  }

  const handleUnBindSuccess = async () => {
    setApp({})
    onUnBindSuccess?.()
  }

  return loading ? null : (
    <AgentGoPublishConf
      form={form}
      mode={mode}
      app={app}
      steps={steps}
      guides={DING_TALK_DOCS}
      serviceAvatar={modeIcon}
      completeDeps={['confirmBeforeComplete']}
      onComplete={handleComplete}
      onValuesChange={handleValuesChange}
      onUnBindSuccess={handleUnBindSuccess}
    >
      {step => (
        <>
          {steps[step].content}
          <AgentGoFormItem
            label={renderLabel('Client ID', true)}
            name='AppKey'
            required
            hidden={step !== 0}
            normalize={(value: string) => value?.trim()}
            rules={[{ required: true, message: 'Client ID 不能为空' }]}
          >
            <Input
              placeholder='在钉钉应用-凭证与基础信息中获取参数'
              spellCheck={false}
            />
          </AgentGoFormItem>
          <AgentGoFormItem
            label={renderLabel('Client Secret', true)}
            name='AppSecret'
            required
            hidden={step !== 0}
            normalize={(value: string) => value?.trim()}
            rules={[{ required: true, message: 'Client Secret 不能为空' }]}
          >
            <Input
              placeholder='在钉钉应用-凭证与基础信息中获取参数'
              spellCheck={false}
            />
          </AgentGoFormItem>
          <AgentGoFormItem
            label={renderLabel('应用名称', true)}
            name='auth_app_name'
            required
            hidden={step !== 0}
            normalize={(value: string) => value?.trim()}
            rules={[{ required: true, message: '应用名称不能为空' }]}
          >
            <Input placeholder='在钉钉应用-凭证与基础信息中获取参数' />
          </AgentGoFormItem>

          <div
            className={classNames(
              'bg-bg_3 bg-op-6 rounded-8px p-12',
              step !== 2 ? 'hidden' : '',
            )}
          >
            <Form.Item
              name='confirmBeforeComplete'
              valuePropName='checked'
              preserve={false}
              hidden={step !== 2}
              noStyle
            >
              <Checkbox className='!mb-4'>
                <span className='text-font text-14px/20px'>钉钉应用已发布</span>
              </Checkbox>
            </Form.Item>
            <p className='text-#979797 text-12px/20px ml-24'>
              未发布的应用将无法正常使用
            </p>
          </div>
        </>
      )}
    </AgentGoPublishConf>
  )
}
